import type { ComboboxItem } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useOrgPortfolios } from '../../../../store/global-store/global-store.hooks';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';

/**
 * Hook to get portfolio options formatted for a Mantine Combobox, excluding the current portfolio
 *
 * @returns {ComboboxData} Array of portfolio options with id/name as value/label pairs
 */
const usePortfolioOptions = (): ComboboxItem[] => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const portfolios = useOrgPortfolios();

  const filteredPortfolios = portfolios.filter((p) => p.id !== portfolio?.id);

  return filteredPortfolios.map((p) => ({
    value: p.id,
    label: p.name,
  }));
};

/**
 * Hook to get placeholder text for portfolio comparison selector based on number of selected portfolios
 *
 * @returns {string} Translated placeholder text showing number of selected portfolios
 */
const usePlaceholderText = (): string => {
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);

  const { t } = useTranslation();

  if (selectedPortfolios.length === 0) {
    return t('process_analysis.main_section.select_for_comparison');
  }

  if (selectedPortfolios.length === 1) {
    return t('process_analysis.main_section.one_portfolio');
  }

  return t('process_analysis.main_section.multiple_portfolios', { count: selectedPortfolios.length });
};

export { usePlaceholderText, usePortfolioOptions };
